/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Injectable, Optional} from '@angular/core';
import {LocalUserProfile} from '../../components/session/local-user-profile';
import * as _ from 'lodash';
import {forkJoin, from, map, Observable, of, switchMap, throwError} from 'rxjs';

import {AssetService} from './asset/asset.service';
import {AdnApiClientService} from '../common/controller/adn-api-client.service';
import {
  SignalAdminResourceApiBase,
  SignalAdminResourceModel,
  SignalAdminResourcePage
} from '../common/controller/signal-admin-resource-api';
import {transformCreativeRequest} from './creative-api-transforms';

export {transformCreativeRequest} from './creative-api-transforms';

export interface CreativeModel extends SignalAdminResourceModel {
  [key: string]: any;
}

export interface CreativeCopyOptions {
  lineItemId?: string;
  creativeSetId?: string;
  creativeId?: string;
  name?: string;
  userState?: string;
  transcodeConfig?: any;
  dimensions?: any;
  objectState?: string;
}

@Injectable({providedIn: 'root'})
export class CreativeApiService extends SignalAdminResourceApiBase<CreativeModel> {
  constructor(client: AdnApiClientService, private readonly assets: AssetService, @Optional() localUserProfile?: LocalUserProfile) {
    super(client, 'creatives', {}, () => localUserProfile?.isReadOnly() || false);
  }

  override save(model: CreativeModel): Observable<CreativeModel> {
    return super.save(transformCreativeRequest(model));
  }

  bulkSave(modelsOrParams: Partial<CreativeModel>[] | Record<string, any>, models?: Partial<CreativeModel>[]): Observable<void> {
    return this.saveMany(models || modelsOrParams as Partial<CreativeModel>[]);
  }

  listOptions(params: Record<string, any> = {}): Observable<SignalAdminResourcePage<CreativeModel>> {
    return this.client.request<SignalAdminResourcePage<CreativeModel>>({
      method: 'GET',
      url: this.resourceUri('creatives'),
      params
    });
  }

  getHydrated(id: string): Observable<CreativeModel> {
    return this.get(id).pipe(switchMap((creative) => this.hydrate(creative)));
  }

  getHydratedPreview(id: string): Observable<CreativeModel> {
    return this.get(id).pipe(switchMap((creative) => {
      const assetIds = _.map(_.filter(creative.layoutParameters, (value: any) => value.url), 'id');
      if (!assetIds.length) {
        return of(creative);
      }
      return from(this.assets.fromCreative(id, assetIds)).pipe(
        map((assets) => ({...creative, assets}))
      );
    }));
  }

  hydrate(creative: CreativeModel): Observable<CreativeModel> {
    if (!creative) {
      return of(creative);
    }
    const libraryCreativeId = _.get(creative, ['libraryCreative', 'id']);
    const ownAssets = from(this.assets.fromCreative(creative.id, undefined));
    const libraryAssets = libraryCreativeId
      ? from(this.assets.fromCreative(libraryCreativeId, undefined))
      : of(undefined);
    return forkJoin({ownAssets, libraryAssets}).pipe(map(({ownAssets: assets, libraryAssets: linkedAssets}) => ({
      ...creative,
      assets,
      ...(libraryCreativeId ? {libraryCreativeAssets: linkedAssets} : {})
    })));
  }

  info(params: Record<string, any> = {}): Observable<SignalAdminResourcePage<CreativeModel>> {
    return this.client.request<SignalAdminResourcePage<CreativeModel>>({
      method: 'GET',
      url: this.resourceUri(['creatives', 'info']),
      params
    });
  }

  copyCreative(source: CreativeModel | string, options: CreativeCopyOptions): Observable<void> {
    const sourceId = typeof source === 'string' ? source : source?.id;
    if (!sourceId) {
      return throwError(() => 'Insufficient parameters for the creative copy');
    }
    const body = {
      id: sourceId,
      creativeSetId: options.creativeSetId || '',
      lineItemId: options.lineItemId || '',
      userState: options.userState || '',
      objectState: options.objectState || null,
      creativeId: options.creativeId || '',
      name: options.name || (typeof source === 'string' ? '' : source.name),
      transcodeConfig: options.transcodeConfig ? JSON.stringify(options.transcodeConfig) : '',
      dimensions: options.dimensions || null
    };
    return this.client.request<void>({
      method: 'POST',
      url: this.resourceUri(['creatives', sourceId, 'copy']),
      params: _.omit(body, 'id'),
      body
    });
  }

  retailAd(lineItemId: string, creativeId: string, layoutId: string, merchant: string, item: string): Observable<CreativeModel> {
    const body = {lineItem: lineItemId, creative: creativeId, layout: layoutId, merchant, item};
    return this.client.request<CreativeModel>({
      method: 'POST',
      url: this.resourceUri(['creatives', 'retail']),
      params: {merchant, item},
      body
    });
  }
}
